<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { ref, computed, onMounted } from 'vue';

/*==================================================================*/
/*================== IMPORTS DOS COMPONENTES =======================*/
/*==================================================================*/

import TelaTabela from '@/components/v2/telas/TelaTabela/TelaTabela.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import ModalConfirmacaoRetornarRequisicaoDeCompra from './ModalConfirmacaoRetornarRequisicaoDeCompra/ModalConfirmacaoRetornarRequisicaoDeCompra.vue';

/*==================================================================*/
/*================== IMPORTS DE SERVICES ===========================*/
/*==================================================================*/

import ERP from '@/api/v2/ERP.mjs';
import View from '@/api/v2/View.mjs';
import RequisicaoDeCompraService from '@/api/v2/services/RequisicaoDeCompraService.js';
import CotacaoDePedidoDeCompraService from '@/api/v2/services/CotacaoDePedidoDeCompraService.js';
import useUsuarioStore from '@/api/v2/stores/UsuarioStore.js';
import useColaboradorStore from '@/api/v2/stores/ColaboradorStore';

/*==================================================================*/
/*================== STORES ========================================*/
/*==================================================================*/

const usuarioStore = useUsuarioStore();
const colaboradorStore = useColaboradorStore();

/*==================================================================*/
/*================== CONTROLES DESATIVADOS =========================*/
/*==================================================================*/

const disable = ref(false);

const alteraDisable = (update = false) => {

    disable.value = update;

};

/*==================================================================*/
/*================== DADOS DE REQUISIÇÕES DE COMPRA ================*/
/*==================================================================*/

const requisicoesDeCompra = ref([]);
const dados = ref([]);

/*==================================================================*/
/*================== ITENS SELECIONADOS NA GRID =====================*/
/*==================================================================*/

const selecionados = ref([]);

const alteraSelecionados = (dados = []) => {

    selecionados.value = dados;

};

/*==================================================================*/
/*================== GRUPO LÓGICO DO USUÁRIO LOGADO =================*/
/*==================================================================*/

const usuarioGrupo = computed(() => {

    switch(usuarioStore?.usuario?.grupoNome){
        case "Administradores":
        case "Compras - Supervisor":
            return "Administrador";
        case "Compras - Operação":
            return "Comprador";
        default:
            return "Padrão";
    }

});

/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = (dadosParaPreparar = []) => {

    dados.value = dadosParaPreparar.map((dado = {}) => ({
        id: dado.id ?? '',
        codigo: dado.codigo ?? '',
        status: dado.status ?? '',
        dataCriacao: dado.dataCriacao ?? '',
        comprador: dado.comprador ?? {},
        jobId: dado.jobId ?? '',
        jobCodigo: dado.jobCodigo ?? '',
        osId: dado.osId ?? '',
        osCodigo: dado.osCodigo ?? '',
        osItemId: dado.osItemId ?? '',
        osItemStatus: dado.osItemStatus ?? '',
        osItemSequencia: dado.osItemSequencia ?? '',
        osItemDescricao: dado.osItemDescricao ?? '',
        osItemDataSolicitacao: dado.osItemDataSolicitacao ?? '',
        osItemDataLiberacaoCompras: dado.osItemDataLiberacaoCompras ?? '',
        osItemDataNecessidade: dado.osItemDataNecessidade ?? '',
        osItemQuantidade: dado.osItemQuantidade ?? 0,
        osItemUnidadeSigla: dado.osItemUnidadeSigla ?? '',
        osItemValorDisponivel: dado.osItemValorDisponivel ?? 0,
        osItemItemID: dado.osItemItemID ?? '',
        osItemItemCodigo: dado.osItemItemCodigo ?? '',
        owners: dado.owners ?? [],
    }));

};

/*==================================================================*/
/*================== MÉTODO DE LOAD DA VIEW ========================*/
/*==================================================================*/

const loadRequisicoesDeCompra = async () => {

    alteraDisable(true);

    selecionados.value = [];

    const response = await ERP.getManyWithView(View.REQUISICAO_DE_COMPRA_CRIAR_COTACAO_DE_PEDIDO_DE_COMPRA);

    requisicoesDeCompra.value = response?.data || [];
    preparaDados(requisicoesDeCompra.value);

    alteraDisable(false);

};

/*==================================================================*/
/*========= FILTROS DE COMPRADOR, JOB, OS E CÓDIGO DO ITEM =========*/
/*==================================================================*/

const dadoCompradorFiltro = ref('');
const dadoJobFiltro = ref('');
const dadoOSFiltro = ref('');
const dadoCodigoItemFiltro = ref('');

const dadoCompradorFiltroValido = (compradorNome) => {

    if(dadoCompradorFiltro.value === ''){
        return true;
    }

    return compradorNome === dadoCompradorFiltro.value;

};

/*==================================================================*/
/*================== VISIBILIDADE POR GRUPO DO USUÁRIO =============*/
/*==================================================================*/

const dadoVisivelParaUsuario = (dado) => {

    if(usuarioGrupo.value === 'Administrador'){
        return true;
    }

    if(usuarioGrupo.value === 'Comprador'){
        return dado?.comprador?.id === colaboradorStore?.colaborador?.id;
    }

    return false;

};

const dadoJobFiltroValido = (jobCodigo) => {

    if(dadoJobFiltro.value === ''){
        return true;
    }

    return jobCodigo === dadoJobFiltro.value;

};

const dadoOSFiltroValido = (osCodigo) => {

    if(dadoOSFiltro.value === ''){
        return true;
    }

    return osCodigo === dadoOSFiltro.value;

};

const dadoCodigoItemFiltroValido = (osItemItemCodigo) => {

    if(dadoCodigoItemFiltro.value === ''){
        return true;
    }

    return osItemItemCodigo === dadoCodigoItemFiltro.value;

};

/*==================================================================*/
/*================== DADOS VISÍVEIS PARA O USUÁRIO =================*/
/*==================================================================*/

const dadosVisiveis = computed(() => {

    return dados?.value?.filter(dadoVisivelParaUsuario) ?? [];

});

/*==================================================================*/
/*================== DADOS FILTRADOS ===============================*/
/*==================================================================*/

const dadosFiltrados = computed(() => {

    return dadosVisiveis?.value?.reduce((acc, dado) => {

        if(!dadoCompradorFiltroValido(dado?.comprador?.nome)){
            return acc;
        }

        if(!dadoJobFiltroValido(dado?.jobCodigo)){
            return acc;
        }

        if(!dadoOSFiltroValido(dado?.osCodigo)){
            return acc;
        }

        if(!dadoCodigoItemFiltroValido(dado?.osItemItemCodigo)){
            return acc;
        }

        acc.push(dado);

        return acc;

    }, []);

});

/*==================================================================*/
/*================== ALTERA FILTROS ================================*/
/*==================================================================*/

const alteraDadoCompradorFiltro = (dado) => {

    dadoCompradorFiltro.value = dado?.comprador?.nome || '';

};

const alteraDadoJobFiltro = (dado) => {

    dadoJobFiltro.value = dado?.jobCodigo || '';

};

const alteraDadoOSFiltro = (dado) => {

    dadoOSFiltro.value = dado?.osCodigo || '';

};

const alteraDadoCodigoItemFiltro = (dado) => {

    dadoCodigoItemFiltro.value = dado?.osItemItemCodigo || '';

};

/*==================================================================*/
/*================== FILTROS CONFIGURADOS ==========================*/
/*==================================================================*/

const filtros = computed(() => [

    { label: 'Job',            items: dadosVisiveis.value, itemText: 'jobCodigo',         onChange: alteraDadoJobFiltro },
    { label: 'OS',             items: dadosVisiveis.value, itemText: 'osCodigo',          onChange: alteraDadoOSFiltro },
    { label: 'Código do Item', items: dadosVisiveis.value, itemText: 'osItemItemCodigo',  onChange: alteraDadoCodigoItemFiltro },
    { label: 'Comprador',      items: dadosVisiveis.value, itemText: 'comprador.nome',    onChange: alteraDadoCompradorFiltro },

]);

/*==================================================================*/
/*================== HEADERS DA TABELA =============================*/
/*==================================================================*/

const dadosHeaders = [
    { text: 'Comprador',                 value: 'comprador.nome',                        type: 'string' },
    { text: 'Job',                       value: 'jobCodigo',                             type: 'string' },
    { text: 'OS',                        value: 'osCodigo',                              type: 'string' },
    { text: 'Sequência',                 value: 'osItemSequencia',                       type: 'string' },
    { text: 'Código do Item',            value: 'osItemItemCodigo',                      type: 'string' },
    { text: 'Descrição',                 value: 'osItemDescricao',                       type: 'string' },
    { text: 'Data de Solicitação',       value: 'osItemDataSolicitacao',                 type: 'date' },
    { text: 'Data de Liberação Compras', value: 'osItemDataLiberacaoCompras',            type: 'date' },
    { text: 'Data de Necessidade',       value: 'osItemDataNecessidade',                 type: 'date' },
    { text: 'Quantidade',                value: 'osItemQuantidade',                      type: 'number' },
    { text: 'Unidade',                   value: 'osItemUnidadeSigla',                    type: 'string' },
    { text: 'Valor Disponível',          value: 'osItemValorDisponivel',                 type: 'money' },
    { text: 'Status (Item)',             value: 'osItemStatus',                          type: 'string' },
];

/*==================================================================*/
/*================== VALIDAÇÃO DOS BOTÕES DE AÇÃO ==================*/
/*==================================================================*/

const disabledBotoesAcao = computed(() => {

    return disable.value || !selecionados.value?.length;

});

/*==================================================================*/
/*================== RETORNAR REQUISIÇÕES PARA O COORDENADOR ========*/
/*==================================================================*/

const modalConfirmacaoRetornarRequisicoesRef = ref(null);

const aoRetornarRequisicoes = async () => {

    if(disabledBotoesAcao.value){
        return;
    }

    await modalConfirmacaoRetornarRequisicoesRef.value?.abrirModal();

};

const confirmaRetornoDeRequisicoes = async (justificativaDigitada) => {

    const requisicoesDeCompraIDs = selecionados.value.map(({ id }) => id);

    const justificativa = `Retornado para LC\nJustificativa:${justificativaDigitada}`;

    const requisicoesRetornadasResponse = await RequisicaoDeCompraService.retornarRequisicoes(requisicoesDeCompraIDs, justificativa);
    if(requisicoesRetornadasResponse.status != 200){
        throw new Error('Falha ao retornar as Requisições para o Coordenador');
    }

    await loadRequisicoesDeCompra();

};

/*==================================================================*/
/*================== CRIAR COTAÇÃO =================================*/
/*==================================================================*/

const aoCriarCotacoes = async () => {

    if(disabledBotoesAcao.value){
        return;
    }

    const requisicoesDeCompraIDs = selecionados.value.map(({ id }) => id);

    const cotacaoResponse = await CotacaoDePedidoDeCompraService.criarCotacao(requisicoesDeCompraIDs, colaboradorStore.colaborador);
    if(cotacaoResponse.status != 200){
        throw new Error('Falha ao criar a Cotação');
    }

    await loadRequisicoesDeCompra();

};

/*==================================================================*/
/*================== ON MOUNTED ====================================*/
/*==================================================================*/

onMounted(async () => {

    await loadRequisicoesDeCompra();

});

</script>

<template>

    <TelaTabela
        :disabled="disable"
        :filtros="filtros"
        :headers="dadosHeaders"
        :dados="dadosFiltrados"
        selecao="multiplo"
        :selecionados="selecionados"
        item-key="id"
        paginacao="paginada"
        @update:selecionados="alteraSelecionados"
    >

        <template #tela-titulo>

            Criar Cotação de Pedido de Compra

        </template>

        <template #toolbar-baixo-direita>

            <div class="c-botoes-acao">

                <BotaoTextoBase
                    class="c-botao-acao"
                    color="#D32F2F"
                    :disabled="disabledBotoesAcao"
                    :click="aoRetornarRequisicoes"
                >
                    Retornar

                    <template #tooltip>
                        Retornar itens para o Coordenador
                    </template>
                </BotaoTextoBase>

                <BotaoTextoBase
                    class="c-botao-acao"
                    :disabled="disabledBotoesAcao"
                    :click="aoCriarCotacoes"
                >
                    Cotar

                    <template #tooltip>
                        Colocar as requisições em uma Cotação
                    </template>
                </BotaoTextoBase>

            </div>

        </template>

        <template #modais>

            <ModalConfirmacaoRetornarRequisicaoDeCompra
                ref="modalConfirmacaoRetornarRequisicoesRef"
                :disabled="disable"
                :confirmar="confirmaRetornoDeRequisicoes"
            ></ModalConfirmacaoRetornarRequisicaoDeCompra>

        </template>

    </TelaTabela>

</template>

<style scoped>

.c-botoes-acao{
    display: flex;
    gap: 10px;
}

.c-botoes-acao > *{
    flex: 1 1 auto;
}

.c-botao-acao{
    width: 100%;
}

</style>
